/**
 * The second consent: Google, on top of the Cognito sign-in.
 *
 * Signing in to Alter is not signing in to Google and this file does not
 * pretend otherwise. The two are separate grants, granted separately, and
 * revoked separately.
 *
 * **What changed on 2026-08-17.** This file used to run Google's *token* flow
 * and keep the result in a closure that died with the tab, so nothing
 * long-lived existed anywhere. That was the right trade until the site became
 * a home-screen app: a standalone web app on iOS gets its own cookie
 * container, there is no Google session in it, and the silent `prompt: ''`
 * renewal that made the old design bearable could never succeed there. The
 * calendar asked for a full login on every single launch.
 *
 * So this now runs the *authorization code* flow. The browser gets a one-time
 * code, hands it to Alter's backend, and the backend keeps the refresh token
 * (see `alter-web/infra/lambda/google/`). The page holds only short-lived
 * access tokens, still in a closure and still nowhere else.
 *
 * **The honest cost.** A refresh token now lives in AWS and outlives every
 * tab. That is a real credential to a year of somebody's calendar, and it was
 * given up deliberately rather than drifted into. What did not change is the
 * size of it: the scope is `calendar.readonly`, the backend refuses a grant
 * that comes back wider, and Disconnect revokes at Google as well as
 * forgetting locally.
 *
 * **The client id is never in this file.** It comes from the same config.json
 * the Cognito pool ids come from, written at deploy time.
 */

/** Read-only, deliberately. A page that cannot write is better than a page
 * that promises not to: the grant itself is what makes it true. */
export const SCOPE = 'https://www.googleapis.com/auth/calendar.readonly';

export const NOT_CONFIGURED =
  'Google Calendar is not set up for this site yet. It needs a Google client id.';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

/** Treat a token as gone this long before Google does. */
const SAFETY_MS = 60_000;

export function clientIdFor(config) {
  const id = config?.googleClientId;
  return typeof id === 'string' && id.trim() ? id.trim() : null;
}

export function isConfigured(config) {
  return clientIdFor(config) !== null;
}

/**
 * The access token, for as long as this tab is open and no longer.
 *
 * `get` answers only while the token is good; `peek` answers even after it has
 * expired, because revoking an expired token on disconnect is still the polite
 * thing to do and Google accepts it.
 */
export function createTokenStore() {
  let token = null;
  let expiresAt = 0;

  return {
    set(grant, now = Date.now()) {
      const seconds = Number(grant?.expires_in);
      token = typeof grant?.access_token === 'string' && grant.access_token ? grant.access_token : null;
      // A grant with no stated lifetime is treated as already gone: asking
      // again costs a round trip, holding a dead token costs a wrong screen.
      expiresAt = token && Number.isFinite(seconds) && seconds > 0 ? now + seconds * 1000 : 0;
      return token;
    },
    get(now = Date.now()) {
      if (!token) return null;
      return now < expiresAt - SAFETY_MS ? token : null;
    },
    peek() {
      return token;
    },
    clear() {
      token = null;
      expiresAt = 0;
    },
  };
}

/**
 * Google Identity Services, loaded once.
 *
 * Not tested: it is a script tag and a load event, and a double narrower than
 * the real one would only prove that the double works.
 */
export function loadGis(doc = document, win = window) {
  if (win.google?.accounts?.oauth2) return Promise.resolve(win.google);

  const existing = doc.querySelector(`script[src="${GIS_SRC}"]`);
  if (existing?.dataset.loading === 'done') return Promise.resolve(win.google);

  return new Promise((resolve, reject) => {
    const script = existing ?? doc.createElement('script');
    script.addEventListener('load', () => {
      script.dataset.loading = 'done';
      if (win.google?.accounts?.oauth2) resolve(win.google);
      else reject(new Error('Google sign-in loaded but is not usable.'));
    });
    script.addEventListener('error', () => reject(new Error('Could not load Google sign-in.')));
    if (!existing) {
      script.src = GIS_SRC;
      script.async = true;
      script.defer = true;
      doc.head.append(script);
    }
  });
}

/**
 * One code client, which yields a one-time code rather than a token.
 *
 * `initCodeClient` is what makes lasting access possible at all: the token
 * flow returns something good for an hour and nothing durable, so a page
 * using it must be able to ask Google again -- which is exactly what a
 * home-screen app on iOS cannot do.
 *
 * `access_type: 'offline'` with `prompt: 'consent'` is what makes Google
 * return a refresh token. Without both it returns none, silently, and the
 * whole arrangement quietly degrades back to asking every launch.
 */
export function createCodeConsent(clientId, google) {
  let settle = null;

  const client = google.accounts.oauth2.initCodeClient({
    client_id: clientId,
    scope: SCOPE,
    ux_mode: 'popup',
    access_type: 'offline',
    prompt: 'consent',
    // Do not fold in scopes this Google account has already granted Alter.
    // The desktop app asks for calendar write and gmail.modify, and without
    // this Google returned all four to the phone -- so the backend would have
    // ended up holding a credential to the whole mailbox in order to show a
    // read-only calendar (2026-08-17).
    include_granted_scopes: false,
    callback: (response) => {
      const done = settle;
      settle = null;
      if (!done) return;
      if (response?.error) done.reject(new Error(response.error_description || response.error));
      else if (!response?.code) done.reject(new Error('Google did not return an authorization code.'));
      else done.resolve(response.code);
    },
    error_callback: (error) => {
      const done = settle;
      settle = null;
      done?.reject(new Error(error?.type === 'popup_closed'
        ? 'The Google window was closed before it finished.'
        : 'Google sign-in did not finish.'));
    },
  });

  return {
    requestCode() {
      if (settle) settle.reject(new Error('Superseded by a newer request.'));
      return new Promise((resolve, reject) => {
        settle = { resolve, reject };
        client.requestCode();
      });
    },
  };
}
